import { Component, ChangeDetectionStrategy, input, output, computed, inject } from '@angular/core';
import { NgClass } from '@angular/common';

import { SvgLibraryIcon, IconLibrary } from '@finnairoyj/fcom-ui-styles';

import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common/interfaces/gtm.interface';
import { UiTranslateModule } from '@fcom/ui-translate/ui-translate.module';

import { IconButtonSize, IconButtonTheme } from '../../buttons/icon-button/enums';
import { NotificationTheme } from '../enums';
import { NotificationI18n } from '../interfaces';
import { IconComponent } from '../../icons/icon/icon.component';
import { LinkComponent } from '../../link/link.component';
import { IconButtonComponent } from '../../buttons/icon-button/icon-button.component';

interface BannerSettings {
  bg: string;
  text: string;
  icon: SvgLibraryIcon;
  iconColour: string;
}

@Component({
  selector: 'fcom-banner',
  styleUrls: ['./banner.component.scss'],
  templateUrl: './banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, IconComponent, LinkComponent, IconButtonComponent, UiTranslateModule],
})
export class BannerNotificationComponent {
  private gtmService = inject(GtmService);

  readonly IconLibrary = IconLibrary;
  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  blackFriday = input<boolean>(false);
  gtmTrackingLabel = input<string>(undefined);
  gtmTrackingContext = input<string>(GaContext.CONTENT);
  horizontal = input<boolean>(true);
  i18n = input<NotificationI18n>({
    closeAriaLabel: 'actions.close',
  });
  isClosable = input<boolean>(false);
  linkText = input<string>();
  linkUrl = input<string>();
  linkOpenInNewWindow = input<boolean>(false);
  theme = input<NotificationTheme>(NotificationTheme.INFO);
  title = input<string>();
  isCampaign = input<boolean>(false);

  closeClick = output<Event>();
  linkClick = output<Event>();

  colours = computed<BannerSettings>(() => {
    const theme = this.theme();

    switch (theme) {
      case NotificationTheme.SUCCESS:
        return {
          bg: 'success-50-bg signal-success-notification-bg',
          text: 'nordic-blue-900-text text-icon-primary',
          icon: SvgLibraryIcon.CHECKMARK_BLOCK,
          iconColour: 'signal-success-notification-border-icon-text',
        };
      case NotificationTheme.WARNING:
        return {
          bg: 'alert-50-bg signal-warning-notification-bg',
          text: 'nordic-blue-900-text text-icon-primary',
          icon: SvgLibraryIcon.ALERT_TRIANGLE,
          iconColour: 'signal-warning-notification-border-icon-text',
        };
      case NotificationTheme.ERROR:
      case NotificationTheme.ALERT:
        return {
          bg: 'error-50-bg signal-error-notification-bg',
          text: 'nordic-blue-900-text text-icon-primary',
          icon: SvgLibraryIcon.ALERT_TRIANGLE,
          iconColour: 'signal-error-notification-border-icon-text',
        };
      case NotificationTheme.PROMOTION:
      case NotificationTheme.CAMPAIGN:
        return {
          bg: this.blackFriday()
            ? 'black-bg signal-promotion-tag-blackfriday-bg'
            : 'heather-700-bg signal-promotion-notification-bg',
          text: 'white-text text-icon-static-light',
          icon: SvgLibraryIcon.DISCOUNT,
          iconColour: 'white-text text-icon-static-light',
        };
      default:
        return {
          bg: 'nordic-blue-50-bg signal-information-notification-bg',
          text: 'nordic-blue-900-text text-icon-primary',
          icon: SvgLibraryIcon.INFO_BLOCK,
          iconColour: 'signal-information-notification-border-icon-text',
        };
    }
  });

  onLinkClick(event: Event): void {
    this.linkClick.emit(event);

    if (this.gtmTrackingLabel()) {
      this.gtmService.trackElement(
        this.gtmTrackingLabel(),
        this.gtmTrackingContext(),
        ElementTypes.BANNER,
        undefined,
        ElementActions.CLICK
      );
    }
  }
}
